import React from 'react';
import moment from 'moment';
import ReactTable from 'react-table';
import Datetime from 'react-datetime';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControl from '@material-ui/core/FormControl';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';

import GridContainer from 'beaktShared/components/Grid/GridContainer.js';
import GridItem from 'beaktShared/components/Grid/GridItem.js';
import Button from 'beaktShared/components/CustomButtons/Button.js';
import SnackbarContent from 'beaktShared/components/Snackbar/SnackbarContent.js';

import { validateDate, convertDate, formateDate, getLocale, getStringFormatDate } from 'beaktShared/helpers/masks';
import { UsePostApi } from 'beaktShared/services/crudeApiService';
import { getTokenCookie } from 'beaktShared/services/session';

import { makeStyles } from '@material-ui/core/styles';
import styles from 'assets/jss/material-dashboard-pro-react/views/crudeStyle.js';

const useStyles = makeStyles(styles);

const tenantId = window.location.host.split(':')[0].split('.')[0];

const opcoesBlocos = [
  { value: null,                label: 'Todos os blocos' },
  { value: 'Dados',             label: 'Dados do Estabelecimento' },
  { value: 'Royalties',         label: 'Royalties / Credenciamento' },
  { value: 'FormaPagamento',    label: 'Forma de Pagamento / Contrato' },
  { value: 'ContatoAdicionais', label: 'Contatos Adicionais' },
  { value: 'Vendas',            label: 'Configurações de Vendas' },
  { value: 'Migracao',          label: 'Migração de Unidade' },
];

const opcoesTipoAcao = [
  { value: null,        label: 'Todos os tipos' },
  { value: 'NOVO',      label: 'Novo Cadastro' },
  { value: 'ALTERAÇÃO', label: 'Alteração' },
  { value: 'MIGRAÇÃO',  label: 'Migração' },
];

const camposLabel = {
  Descricao:               'Nome Fantasia',
  CNPJCPF:                 'CNPJ/CPF',
  RazaoSocial:             'Razão Social',
  IE:                      'Inscrição Estadual',
  Ativo:                   'Ativo no sistema',
  Telefone:                'Telefone',
  Email:                   'E-mail',
  WhatsApp:                'WhatsApp',
  Rua:                     'Rua',
  Numero:                  'Número',
  Complemento:             'Complemento',
  Bairro:                  'Bairro',
  Cidade:                  'Cidade',
  Estado:                  'Estado',
  CEP:                     'CEP',
  NomeResponsavel:         'Responsável Principal',
  dtCredenciamento:        'Data de Credenciamento',
  dtInauguracao:           'Data de Inauguração',
  Justificativa:           'Justificativa',
  PercentualRoyalties:     'Royalties (%)',
  MinRoyalties:            'Royalties Mínimo',
  MaxRoyalties:            'Royalties Máximo',
  FixoRoyalties:           'Royalties Fixo',
  BloqueioPedidoCompra:    'Bloqueio Pedido de Compra',
  PagamentoImediato:       'Pagamento Imediato',
  ControleCobranca:        'Controle de Cobrança',
  ControlaEstoque:         'Controla Estoque',
  AtividadePrincipal:      'Atividade Principal',
  CodigoCredenciamentoUra: 'Código de Credenciamento URA',
};

const thStyle = {
  textAlign: 'left',
  padding: '6px 12px',
  borderBottom: '2px solid #e0e0e0',
  fontWeight: 700,
  fontSize: 12,
  color: '#555',
};

const tdStyle = {
  padding: '6px 12px',
  borderBottom: '1px solid #f0f0f0',
  fontSize: 13,
  verticalAlign: 'middle',
};

const diffContatos = (jsonDe, jsonPara) => {
  try {
    const antes  = jsonDe  ? JSON.parse(jsonDe)  : [];
    const depois = jsonPara ? JSON.parse(jsonPara) : [];
    const resultado = [];

    depois.forEach(c => {
      const existia = antes.find(a => a.EstabelecimentoContatoAdicionalId === c.EstabelecimentoContatoAdicionalId);
      if (!existia) {
        resultado.push({ tipo: 'ADICIONADO', contato: c });
      } else if (existia.Nome !== c.Nome || existia.Celular !== c.Celular || existia.Email !== c.Email) {
        resultado.push({ tipo: 'ALTERADO', contato: c, antes: existia });
      }
    });

    antes.forEach(c => {
      if (!depois.find(d => d.EstabelecimentoContatoAdicionalId === c.EstabelecimentoContatoAdicionalId)) {
        resultado.push({ tipo: 'REMOVIDO', contato: c });
      }
    });

    return resultado;
  } catch { return []; }
};

const camposFormaPagamento = {
  PercentualSplit:  '% Split',
  Provisionamento:  'Provisionamento',
  ControleCobranca: 'Controle de Cobrança',
};

const diffFormaPagamento = (jsonDe, jsonPara) => {
  try {
    const antes  = jsonDe  ? JSON.parse(jsonDe)  : {};
    const depois = jsonPara ? JSON.parse(jsonPara) : {};

    const diferencasCampos = Object.keys(camposFormaPagamento)
      .filter(campo => {
        const vA = antes[campo]  !== undefined ? antes[campo]  : null;
        const vD = depois[campo] !== undefined ? depois[campo] : null;
        const nA = parseFloat(vA), nD = parseFloat(vD);
        if (!isNaN(nA) && !isNaN(nD)) return nA !== nD;
        return String(vA) !== String(vD);
      })
      .map(campo => ({
        label:  camposFormaPagamento[campo],
        antes:  antes[campo]  !== undefined ? String(antes[campo])  : '—',
        depois: depois[campo] !== undefined ? String(depois[campo]) : '—',
      }));

    const idsAntes  = new Set(antes.FormaPagamentos  || []);
    const idsDepois = new Set(depois.FormaPagamentos || []);
    const adicionados = [...idsDepois].filter(id => !idsAntes.has(id));
    const removidos   = [...idsAntes].filter(id => !idsDepois.has(id));

    return { diferencasCampos, adicionados, removidos };
  } catch {
    return { diferencasCampos: [], adicionados: [], removidos: [] };
  }
};

const camposVendas = {
  PercentualMaximoLimiteDesconto: '% Máx. Desconto',
  PercentualMaximoLimiteServico:  '% Máx. Serviço',
  PercentualMaximoLimitePacote:   '% Máx. Pacote',
  AvaliacaoObrigatoriaPacote:     'Avaliação Obrigatória (Pacote)',
  ControlaEstoque:                'Controla Estoque',
};

const diffVendas = (jsonDe, jsonPara) => {
  try {
    const antes  = jsonDe  ? JSON.parse(jsonDe)  : {};
    const depois = jsonPara ? JSON.parse(jsonPara) : {};

    const diferencasCampos = Object.keys(camposVendas)
      .filter(campo => String(antes[campo] ?? null) !== String(depois[campo] ?? null))
      .map(campo => ({
        label:  camposVendas[campo],
        antes:  antes[campo]  != null ? String(antes[campo])  : '—',
        depois: depois[campo] != null ? String(depois[campo]) : '—',
      }));

    const diffLista = (antesArr, depoisArr, idKey, labelFn) => {
      const idsAntes  = new Set((antesArr  || []).map(i => i[idKey]));
      const idsDepois = new Set((depoisArr || []).map(i => i[idKey]));
      return {
        adicionados: (depoisArr || []).filter(i => !idsAntes.has(i[idKey])).map(labelFn),
        removidos:   (antesArr  || []).filter(i => !idsDepois.has(i[idKey])).map(labelFn),
      };
    };

    // Logs novos trazem { Id, Descricao } "achatado"; logs antigos (gravados antes dessa
    // mudança) ainda trazem a entidade aninhada (ex.: i.Produto.Descricao) — aceita os dois.
    return {
      diferencasCampos,
      produtos:       diffLista(antes.EstabelecimentoProdutos,       depois.EstabelecimentoProdutos,       'ProdutoId',       i => i.Descricao || i.Produto?.Descricao           || `Produto ID ${i.ProdutoId}`),
      servicos:       diffLista(antes.EstabelecimentoServicos,       depois.EstabelecimentoServicos,       'ServicoId',       i => i.Descricao || i.Servico?.Descricao           || `Serviço ID ${i.ServicoId}`),
      pacotes:        diffLista(antes.EstabelecimentoPacotes,        depois.EstabelecimentoPacotes,        'PacoteId',        i => i.Descricao || i.Pacote?.Descricao            || `Pacote ID ${i.PacoteId}`),
      especialidades: diffLista(antes.EstabelecimentoEspecialidades, depois.EstabelecimentoEspecialidades, 'EspecialidadeId', i => i.Descricao || i.Especialidade?.Descricao      || `Especialidade ID ${i.EspecialidadeId}`),
    };
  } catch {
    const vazio = { adicionados: [], removidos: [] };
    return { diferencasCampos: [], produtos: vazio, servicos: vazio, pacotes: vazio, especialidades: vazio };
  }
};

const formatValueIfDate = (value) => {
  if (!value || value === '—') return value;
  const lower = String(value).toLowerCase();
  if (lower === 'true')  return 'ATIVO';
  if (lower === 'false') return 'INATIVO';
  const m = moment(value, ['YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DDTHH:mm:ssZ', 'YYYY-MM-DD'], true);
  if (m.isValid()) return m.format('DD/MM/YYYY');
  return value;
};

const diffJson = (jsonDe, jsonPara) => {
  if (!jsonPara) return [];
  try {
    const antes  = jsonDe ? JSON.parse(jsonDe) : {};
    const depois = JSON.parse(jsonPara);

    return Object.keys(camposLabel)
      .filter(campo => {
        const vAntes  = antes[campo]  !== undefined ? String(antes[campo])  : '';
        const vDepois = depois[campo] !== undefined ? String(depois[campo]) : '';
        return vAntes !== vDepois;
      })
      .map(campo => ({
        campo,
        label:        camposLabel[campo],
        antes:        antes[campo]  !== undefined ? String(antes[campo])  : '—',
        depois:       depois[campo] !== undefined ? String(depois[campo]) : '—',
        isCampoAtivo: campo === 'Ativo',
      }));
  } catch { return []; }
};

export default function LogCredenciamento({ item, CallHandleVoltar }) {
  const { t } = useTranslation();
  const classes = useStyles();

  const [dtini, setDtini]               = React.useState(formateDate(moment(new Date()).add(-30, 'days')));
  const [dtfim, setDtfim]               = React.useState(formateDate(moment(new Date())));
  const [bloco, setBloco]               = React.useState(null);
  const [tipoAcao, setTipoAcao]         = React.useState(null);
  const [logs, setLogs]                 = React.useState([]);
  const [loading, setLoading]           = React.useState(false);
  const [buscaFeita, setBuscaFeita]     = React.useState(false);
  const [loadingXlsx, setLoadingXlsx]   = React.useState(false);
  const [notification, setNotification] = React.useState({ show: false, color: 'info', message: '' });

  const showNotification = (message, color) => {
    setNotification({ show: true, color, message });
    setTimeout(() => setNotification(prev => ({ ...prev, show: false })), 6000);
  };

  const toDateStr = (d) => {
    if (!d) return '';
    if (typeof d === 'object' && d.format) return d.format('DD/MM/YYYY');
    return d;
  };

  const monthDiff = (dateFrom, dateTo) => {
    return dateTo.getMonth() - dateFrom.getMonth() +
      (12 * (dateTo.getFullYear() - dateFrom.getFullYear()));
  };

  const validarDatas = () => {
    const dtiniStr = toDateStr(dtini);
    const dtfimStr = toDateStr(dtfim);

    if (!dtiniStr) { showNotification('Data inicial obrigatória', 'warning'); return null; }
    if (!dtfimStr) { showNotification('Data final obrigatória', 'warning'); return null; }
    if (!validateDate(dtiniStr)) { showNotification('Data inicial inválida', 'warning'); return null; }
    if (!validateDate(dtfimStr)) { showNotification('Data final inválida', 'warning'); return null; }
    if (monthDiff(convertDate(dtiniStr), convertDate(dtfimStr)) > 3) {
      showNotification(t('error_message.select_3month'), 'danger');
      return null;
    }

    return { dtiniStr, dtfimStr };
  };

  const buildFiltro = (dtiniStr, dtfimStr) => ({
    estabelecimentoId: item.estabelecimentoId,
    dtIni: dtiniStr,
    dtFim: dtfimStr,
    bloco: bloco?.value || null,
    tipoAcao: tipoAcao?.value || null,
  });

  const buscarLogs = () => {
    const datas = validarDatas();
    if (!datas) return;

    setLoading(true);
    setBuscaFeita(false);

    UsePostApi('Estabelecimentos/logCredenciamento', buildFiltro(datas.dtiniStr, datas.dtfimStr)).then(result => {
      setLoading(false);
      setBuscaFeita(true);
      if (result.status === 200) {
        setLogs(result.data);
      } else {
        showNotification('Erro ao carregar logs', 'danger');
      }
    });
  };

  const exportarXlsx = async () => {
    const datas = validarDatas();
    if (!datas) return;

    const token = getTokenCookie();
    if (!token || !token.accessToken) {
      showNotification('Sessão expirada.', 'danger');
      return;
    }

    setLoadingXlsx(true);

    const url = `${process.env.REACT_APP_API_ENDPOINT}api/Estabelecimentos/logCredenciamento/export`;
    const filtro = buildFiltro(datas.dtiniStr, datas.dtfimStr);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token.accessToken}`,
          tenantId: tenantId,
        },
        body: JSON.stringify(filtro),
      });
      if (!response.ok) throw new Error('Erro ao gerar exportação');

      const blob    = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a       = document.createElement('a');
      a.href        = blobUrl;
      a.download    = `LogCredenciamento_${moment().format('DDMMYYYYHHmmss')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      showNotification('Erro ao exportar. Tente novamente.', 'danger');
    } finally {
      setLoadingXlsx(false);
    }
  };

  const columns = [
    {
      Header: 'Data / Hora',
      accessor: 'dataAtividade',
      width: 160,
      Cell: ({ value }) => moment(value).format('DD/MM/YYYY HH:mm:ss'),
    },
    {
      Header: 'Usuário',
      accessor: 'nomeUsuario',
      width: 180,
    },
    {
      Header: 'Tipo de Ação',
      accessor: 'tipoAcao',
      width: 130,
      Cell: ({ value }) => {
        const conf = ({
          'NOVO':      { bg: '#e8f5e9', color: '#2e7d32' },
          'ALTERAÇÃO': { bg: '#e3f2fd', color: '#1565c0' },
          'MIGRAÇÃO':  { bg: '#f3e5f5', color: '#6a1b9a' },
        })[value] || { bg: '#eceff1', color: '#455a64' };
        return (
          <span style={{ background: conf.bg, color: conf.color, borderRadius: 4, padding: '2px 8px', fontWeight: 600, fontSize: 12 }}>
            {value}
          </span>
        );
      },
    },
    {
      Header: 'Bloco',
      accessor: 'bloco',
      width: 220,
      Cell: ({ value }) => {
        const labels = {
          'Dados':             'Dados do Estabelecimento',
          'Royalties':         'Royalties / Credenciamento',
          'FormaPagamento':    'Forma de Pagamento',
          'ContatoAdicionais': 'Contatos Adicionais',
          'Vendas':            'Configurações de Vendas',
          'Migracao':          'Migração de Unidade',
        };
        return labels[value] || value || '—';
      },
    },
  ];

  return (
    <div style={{ width: '100%', padding: '20px' }}>

      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <div className="subTitulo">
            <h6>Histórico do Credenciado</h6>
          </div>
        </GridItem>
      </GridContainer>

      {/* Filtros */}
      <GridContainer>
        <GridItem xs={12} sm={6} md={3}>
          <FormControl fullWidth className={classes.formControl}>
            <label className="select__label">Data Inicial *</label>
            <Datetime
              locale={getLocale()}
              dateFormat={getStringFormatDate()}
              timeFormat={false}
              defaultValue={dtini}
              value={dtini}
              onChange={date => { setDtini(date); }}
              inputProps={{ readOnly: 'readonly' }}
            />
          </FormControl>
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <FormControl fullWidth className={classes.formControl}>
            <label className="select__label">Data Final *</label>
            <Datetime
              locale={getLocale()}
              dateFormat={getStringFormatDate()}
              timeFormat={false}
              defaultValue={dtfim}
              value={dtfim}
              onChange={date => { setDtfim(date); }}
              inputProps={{ readOnly: 'readonly' }}
            />
          </FormControl>
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <FormControl fullWidth className={classes.formControl}>
            <label className="select__label">Bloco</label>
            <Select
              classNamePrefix="select"
              options={opcoesBlocos}
              value={bloco}
              onChange={setBloco}
              placeholder="Todos os blocos"
            />
          </FormControl>
        </GridItem>
        <GridItem xs={12} sm={6} md={3}>
          <FormControl fullWidth className={classes.formControl}>
            <label className="select__label">Tipo de Ação</label>
            <Select
              classNamePrefix="select"
              options={opcoesTipoAcao}
              value={tipoAcao}
              onChange={setTipoAcao}
              placeholder="Todos os tipos"
            />
          </FormControl>
        </GridItem>
      </GridContainer>

      {/* Botões */}
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <Button round color="primary" onClick={buscarLogs} disabled={loading}>
            {loading
              ? <><CircularProgress size={16} color="inherit" style={{ marginRight: 6 }} />Buscando...</>
              : 'Buscar'}
          </Button>
          <Button
            round
            color="success"
            onClick={exportarXlsx}
            disabled={loadingXlsx || !buscaFeita || !logs.length}
            style={{ marginLeft: 8 }}
          >
            {loadingXlsx
              ? <><CircularProgress size={16} color="inherit" style={{ marginRight: 6 }} />Exportando...</>
              : 'Exportar XLSX'}
          </Button>
        </GridItem>

        {notification.show && (
          <GridItem xs={12}>
            <SnackbarContent
              message={notification.message}
              color={notification.color}
              closeNotification={() => setNotification(prev => ({ ...prev, show: false }))}
              close
            />
          </GridItem>
        )}
      </GridContainer>

      {/* Resultados */}
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          {loading && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <CircularProgress />
            </div>
          )}

          {!loading && buscaFeita && logs.length === 0 && (
            <p style={{ textAlign: 'center', margin: '24px 0', color: '#757575' }}>
              Nenhum registro de log encontrado para os filtros selecionados.
            </p>
          )}

          {!loading && logs.length > 0 && (
            <ReactTable

              data={logs}
              columns={columns}
              defaultPageSize={10}
              minRows={5}
              showPaginationBottom
              className="-striped -highlight"
              previousText="Anterior"
              nextText="Próximo"
              loadingText="Carregando..."
              noDataText="Nenhum registro encontrado!"
              pageText="Página"
              ofText="de"
              rowsText="linhas"
              SubComponent={({ original }) => {
                if (original.bloco === 'ContatoAdicionais') {
                  const contatoDiffs = diffContatos(original.jsonDe, original.jsonPara);
                  if (!contatoDiffs.length) {
                    return <div style={{ padding: '12px 24px', color: '#999' }}>Sem alterações nos contatos adicionais.</div>;
                  }
                  const tipoConf = {
                    ADICIONADO: { label: 'Adicionado', bg: '#e8f5e9', color: '#2e7d32' },
                    REMOVIDO:   { label: 'Removido',   bg: '#fce4ec', color: '#c62828' },
                    ALTERADO:   { label: 'Alterado',   bg: '#e3f2fd', color: '#1565c0' },
                  };
                  return (
                    <div style={{ padding: '12px 24px' }}>
                      <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={thStyle}>Status</th>
                            <th style={thStyle}>Nome</th>
                            <th style={thStyle}>Celular</th>
                            <th style={thStyle}>E-mail</th>
                          </tr>
                        </thead>
                        <tbody>
                          {contatoDiffs.map((d, i) => {
                            const conf = tipoConf[d.tipo];
                            return (
                              <tr key={i} style={{ background: conf.bg }}>
                                <td style={tdStyle}>
                                  <span style={{ background: conf.color, color: '#fff', borderRadius: 4, padding: '2px 8px', fontWeight: 600, fontSize: 11 }}>
                                    {conf.label}
                                  </span>
                                </td>
                                <td style={tdStyle}>{d.contato.Nome}</td>
                                <td style={tdStyle}>{d.contato.Celular}</td>
                                <td style={tdStyle}>{d.contato.Email}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                }

                if (original.bloco === 'FormaPagamento') {
                  const { diferencasCampos, adicionados, removidos } = diffFormaPagamento(original.jsonDe, original.jsonPara);
                  if (!diferencasCampos.length && !adicionados.length && !removidos.length) {
                    return <div style={{ padding: '12px 24px', color: '#999' }}>Sem alterações na forma de pagamento.</div>;
                  }
                  return (
                    <div style={{ padding: '12px 24px' }}>
                      {diferencasCampos.length > 0 && (
                        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse', marginBottom: 12 }}>
                          <thead>
                            <tr>
                              <th style={thStyle}>Campo</th>
                              <th style={thStyle}>Valor Anterior</th>
                              <th style={thStyle}>Valor Novo</th>
                            </tr>
                          </thead>
                          <tbody>
                            {diferencasCampos.map((d, i) => (
                              <tr key={i}>
                                <td style={tdStyle}><strong>{d.label}</strong></td>
                                <td style={{ ...tdStyle, color: '#c62828', textDecoration: 'line-through' }}>{formatValueIfDate(d.antes)}</td>
                                <td style={{ ...tdStyle, color: '#2e7d32', fontWeight: 600 }}>{formatValueIfDate(d.depois)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                      {(adicionados.length > 0 || removidos.length > 0) && (
                        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              <th style={thStyle}>Forma de Pagamento (ID)</th>
                              <th style={thStyle}>Operação</th>
                            </tr>
                          </thead>
                          <tbody>
                            {adicionados.map(id => (
                              <tr key={`add-${id}`} style={{ background: '#e8f5e9' }}>
                                <td style={tdStyle}>{id}</td>
                                <td style={tdStyle}>
                                  <span style={{ background: '#2e7d32', color: '#fff', borderRadius: 4, padding: '2px 8px', fontWeight: 600, fontSize: 11 }}>ADICIONADO</span>
                                </td>
                              </tr>
                            ))}
                            {removidos.map(id => (
                              <tr key={`rem-${id}`} style={{ background: '#fce4ec' }}>
                                <td style={tdStyle}>{id}</td>
                                <td style={tdStyle}>
                                  <span style={{ background: '#c62828', color: '#fff', borderRadius: 4, padding: '2px 8px', fontWeight: 600, fontSize: 11 }}>REMOVIDO</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  );
                }

                if (original.bloco === 'Vendas') {
                  const { diferencasCampos, produtos, servicos, pacotes, especialidades } = diffVendas(original.jsonDe, original.jsonPara);
                  const temAlteracao = diferencasCampos.length || produtos.adicionados.length || produtos.removidos.length || servicos.adicionados.length || servicos.removidos.length || pacotes.adicionados.length || pacotes.removidos.length || especialidades.adicionados.length || especialidades.removidos.length;
                  if (!temAlteracao) {
                    return <div style={{ padding: '12px 24px', color: '#999' }}>Sem alterações nas configurações de vendas.</div>;
                  }
                  const renderLista = (titulo, { adicionados, removidos }) => {
                    if (!adicionados.length && !removidos.length) return null;
                    return (
                      <div style={{ marginBottom: 16 }}>
                        <strong style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>{titulo}</strong>
                        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              <th style={thStyle}>Item</th>
                              <th style={thStyle}>Operação</th>
                            </tr>
                          </thead>
                          <tbody>
                            {adicionados.map((desc, i) => (
                              <tr key={`add-${i}`} style={{ background: '#e8f5e9' }}>
                                <td style={tdStyle}>{desc}</td>
                                <td style={tdStyle}><span style={{ background: '#2e7d32', color: '#fff', borderRadius: 4, padding: '2px 8px', fontWeight: 600, fontSize: 11 }}>ADICIONADO</span></td>
                              </tr>
                            ))}
                            {removidos.map((desc, i) => (
                              <tr key={`rem-${i}`} style={{ background: '#fce4ec' }}>
                                <td style={tdStyle}>{desc}</td>
                                <td style={tdStyle}><span style={{ background: '#c62828', color: '#fff', borderRadius: 4, padding: '2px 8px', fontWeight: 600, fontSize: 11 }}>REMOVIDO</span></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  };
                  return (
                    <div style={{ padding: '12px 24px' }}>
                      {diferencasCampos.length > 0 && (
                        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse', marginBottom: 16 }}>
                          <thead>
                            <tr>
                              <th style={thStyle}>Campo</th>
                              <th style={thStyle}>Valor Anterior</th>
                              <th style={thStyle}>Valor Novo</th>
                            </tr>
                          </thead>
                          <tbody>
                            {diferencasCampos.map((d, i) => (
                              <tr key={i}>
                                <td style={tdStyle}><strong>{d.label}</strong></td>
                                <td style={{ ...tdStyle, color: '#c62828', textDecoration: 'line-through' }}>{formatValueIfDate(d.antes)}</td>
                                <td style={{ ...tdStyle, color: '#2e7d32', fontWeight: 600 }}>{formatValueIfDate(d.depois)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                      {renderLista('Produtos', produtos)}
                      {renderLista('Serviços', servicos)}
                      {renderLista('Pacotes', pacotes)}
                      {renderLista('Especialidades', especialidades)}
                    </div>
                  );
                }

                const diffs = diffJson(original.jsonDe, original.jsonPara);
                if (!diffs.length) {
                  return (
                    <div style={{ padding: '12px 24px', color: '#999' }}>
                      {original.tipoAcao === 'NOVO'
                        ? 'Cadastro inicial — ver JSON completo.'
                        : 'Sem campos mapeados alterados.'}
                    </div>
                  );
                }
                return (
                  <div style={{ padding: '12px 24px' }}>
                    <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={thStyle}>Campo</th>
                          <th style={thStyle}>Valor Anterior</th>
                          <th style={thStyle}>Valor Novo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {diffs.map(d => (
                          <tr key={d.campo} style={d.isCampoAtivo ? { background: '#fce4ec' } : {}}>
                            <td style={tdStyle}>
                              <strong>{d.label}</strong>
                              {d.isCampoAtivo && (
                                <span style={{ marginLeft: 8, background: '#c62828', color: '#fff', borderRadius: 4, padding: '1px 6px', fontSize: 11 }}>
                                  ATIVAÇÃO/INATIVAÇÃO
                                </span>
                              )}
                            </td>
                            <td style={{ ...tdStyle, color: '#c62828', textDecoration: 'line-through' }}>{formatValueIfDate(d.antes)}</td>
                            <td style={{ ...tdStyle, color: '#2e7d32', fontWeight: 600 }}>{formatValueIfDate(d.depois)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              }}
            />
          )}
        </GridItem>
      </GridContainer>

      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          <center style={{ marginTop: 66 }}>
            <Button onClick={() => CallHandleVoltar()} color="transparent" >
                {t('label.return')}
            </Button>
          </center>
        </GridItem>
      </GridContainer>

    </div>
  );
}
